import {getCabinetSimilarFeatureFromAnotherColor} from "./cabinetFeatureMatcher";

// Keep this helper independent from the legacy constants dependency graph.
const CABINET_BOX_PART_IDS = [
  "lower_cabinet_boxes",
  "upper_cabinet_boxes",
  "tall_cabinet_boxes",
  "vanitie_cabinet_boxes",
  "vanity_cabinet_boxes",
  "vanity_boxes",
  "top_cabinet_boxes",
  "wall_cabinet_boxes",
  "linen_cabinet_boxes",
  "linen_vanity_boxes",
];

const idsMatch = (firstId, secondId) => String(firstId) === String(secondId);

const updateFeatureEntry = (features, partId, featureId, oldFeatureId) => {
  if (!Array.isArray(features)) {
    return features;
  }

  let hasChanges = false;
  const updatedFeatures = features.map(feature => {
    const matchesPart = feature.partId
      ? feature.partId === partId
      : oldFeatureId && idsMatch(feature.featureId, oldFeatureId);

    if (!matchesPart || idsMatch(feature.featureId, featureId)) {
      return feature;
    }

    hasChanges = true;
    return {...feature, featureId};
  });

  return hasChanges ? updatedFeatures : features;
};

// A cart item can represent either the shape itself or its parent material/style.
// Keep those IDs separate so cabinet color synchronization cannot replace the cabinet box model.
export const syncPersistedSqftShapeFeature = (shape, desiredFeature) => {
  if (!shape || !desiredFeature?.featureId || !desiredFeature.partId) {
    return shape;
  }

  const hasMatchingFeatureEntry = Array.isArray(shape.features)
    && shape.features.some(feature => feature.partId === desiredFeature.partId);
  const isShapeFeature = desiredFeature.partId === shape.partId
    || (!shape.partId && hasMatchingFeatureEntry);
  const isParentFeature = desiredFeature.partId === shape.parentPartId;

  if (!isShapeFeature && !isParentFeature) {
    return shape;
  }

  let updatedFeatures = updateFeatureEntry(
    shape.features,
    desiredFeature.partId,
    desiredFeature.featureId,
    desiredFeature.oldFeatureId
  );
  let featureId = shape.featureId;
  let relatedParentFeatureId = shape.relatedParentFeatureId;

  if (isParentFeature) {
    relatedParentFeatureId = desiredFeature.featureId;
    if (desiredFeature.shapeFeatureId) {
      featureId = desiredFeature.shapeFeatureId;
      updatedFeatures = updateFeatureEntry(
        updatedFeatures,
        shape.partId,
        desiredFeature.shapeFeatureId,
        shape.featureId
      );
    }
  } else {
    featureId = desiredFeature.featureId;
  }

  const hasChanges = !idsMatch(featureId, shape.featureId)
    || !idsMatch(relatedParentFeatureId, shape.relatedParentFeatureId)
    || updatedFeatures !== shape.features;

  if (!hasChanges) {
    return shape;
  }

  return isParentFeature
    ? {...shape, featureId, relatedParentFeatureId, features: updatedFeatures}
    : {...shape, featureId, features: updatedFeatures};
};

export const resolveRelatedShapeFeatureId = (shape, parentFeature, layout, currentParentFeature = null) => {
  if (!shape || !parentFeature || !CABINET_BOX_PART_IDS.includes(shape.partId)) {
    return null;
  }

  const relatedFeatures = typeof parentFeature.getFeatureRelatedFeatures === "function"
    ? parentFeature.getFeatureRelatedFeatures()
    : null;
  const currentShapeFeature = layout && typeof layout.getFeatureById === "function"
    ? layout.getFeatureById(shape.featureId, true)
    : null;
  const currentParentRelatedFeatures = typeof currentParentFeature?.getFeatureRelatedFeatures === "function"
    ? currentParentFeature.getFeatureRelatedFeatures()
    : null;
  const currentShapeDbFeature = currentShapeFeature?.dbFeature
    || (Array.isArray(currentParentRelatedFeatures)
      ? currentParentRelatedFeatures.find(feature => idsMatch(feature.id, shape.featureId))
      : null);

  if (!currentShapeDbFeature || !Array.isArray(relatedFeatures)) {
    return null;
  }

  const matchingFeature = getCabinetSimilarFeatureFromAnotherColor(
    relatedFeatures,
    currentShapeDbFeature
  );

  return matchingFeature?.id || null;
};
